
import React from 'react';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Edit, Save, X } from 'lucide-react';
import DeviceGroupsTable from './DeviceGroupsTable';
import DeviceGroupsSearch from './DeviceGroupsSearch';
import AddDeviceGroupDialog from './AddDeviceGroupDialog';
import { useDeviceGroups } from './useDeviceGroups';

interface DeviceGroupsSettingsProps {
  editing: boolean;
  onEditToggle: () => void;
  onSave: () => void;
}

const DeviceGroupsSettings: React.FC<DeviceGroupsSettingsProps> = ({ 
  editing, 
  onEditToggle, 
  onSave 
}) => {
  const {
    groups,
    filteredGroups,
    searchTerm,
    setSearchTerm,
    addDialogOpen,
    setAddDialogOpen,
    handleAddGroup,
    handleDeleteGroup
  } = useDeviceGroups();

  return (
    <Card>
      <CardHeader className="pb-2 flex flex-row justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Device Groups</h2>
          <p className="text-muted-foreground">
            Organize devices into logical groups for easier management
          </p>
        </div>
        <div className="flex gap-2">
          {editing ? (
            <>
              <Button variant="outline" onClick={onEditToggle} className="gap-1">
                <X className="h-4 w-4" />
                Cancel
              </Button>
              <Button onClick={onSave} className="gap-1">
                <Save className="h-4 w-4" />
                Save Changes
              </Button>
            </>
          ) : (
            <>
              <AddDeviceGroupDialog 
                open={addDialogOpen} 
                onOpenChange={setAddDialogOpen} 
                onAdd={handleAddGroup} 
              />
              <Button variant="outline" onClick={onEditToggle} className="gap-1">
                <Edit className="h-4 w-4" />
                Edit
              </Button>
            </>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <DeviceGroupsSearch
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
        />

        <DeviceGroupsTable 
          groups={filteredGroups} 
          editing={editing}
          onDelete={handleDeleteGroup}
        />
      </CardContent>
    </Card>
  );
};

export default DeviceGroupsSettings;
